import { useEffect, useState } from "react";

/**
 * useMatchMedia
 *
 * A small wrapper around `window.matchMedia` that is safe in SSR/JSDOM.
 * - Defaults to `defaultValue` when matchMedia is unavailable.
 * - Subscribes to media query changes (modern + Safari fallback).
 *
 * Note: Jest setup mocks `window.matchMedia` with `matches: false` by default,
 * so callers default to desktop behavior unless a test overrides the mock.
 */
const useMatchMedia = (query, defaultValue = false) => {
  const [matches, setMatches] = useState(() => {
    try {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return !!defaultValue;
      }
      return !!window.matchMedia(query).matches;
    } catch {
      return !!defaultValue;
    }
  });

  useEffect(() => {
    try {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
      const mql = window.matchMedia(query);

      const handler = (e) => setMatches(!!e.matches);
      // Ensure state is in sync on mount (query may have changed).
      setMatches(!!mql.matches);

      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
      }

      // Safari / older browsers.
      if (typeof mql.addListener === "function") {
        mql.addListener(handler);
        return () => mql.removeListener(handler);
      }
    } catch {
      // ignore
    }
    return undefined;
  }, [query]);

  return matches;
};

export { useMatchMedia };
export default useMatchMedia;

